import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import QuizService from "../../../services/KangarooServices/QuizService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import "../css/Quiz.css";
import { formatDate } from "../../../Components/Common/FormatDate";
/* ── Score Ring ── */
export function ScoreRing({ percentage, pass }) {
  const r = 38;
  const circ = 2 * Math.PI * r;
  const dash = (Math.min(percentage, 100) / 100) * circ;
  return (
    <div className="score-ring">
      <svg width="90" height="90" viewBox="0 0 90 90">
        <circle
          cx="45"
          cy="45"
          r={r}
          fill="none"
          stroke="#dee2e6"
          strokeWidth="7"
        />
        <circle
          cx="45"
          cy="45"
          r={r}
          fill="none"
          stroke={pass ? "rgb(123,31,162)" : "#dc3545"}
          strokeWidth="7"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="score-ring-label">
        {percentage}%<small>score</small>
      </div>
    </div>
  );
}

/* ── Result Icon ── */
export function ResultIcon({ type }) {
  if (type === "correct")
    return <div className="result-icon bg-success text-white">✓</div>;
  if (type === "wrong")
    return <div className="result-icon bg-danger text-white">✕</div>;
  return <div className="result-dot" />;
}

/* ── Question Result Card ── */
export function QuestionCard({ result, index, allOptions }) {
  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body p-4">
        {/* Meta */}
        <div className="d-flex align-items-center gap-2 mb-2">
          <span
            className="text-uppercase fw-semibold"
            style={{
              fontSize: "10px",
              color: "rgb(123, 31, 162)",
              letterSpacing: "0.1em",
            }}
          >
            Question {index + 1}
          </span>
          <span
            className={`badge rounded-pill ${
              result.isCorrect ? "text-bg-success" : "text-bg-danger"
            }`}
            style={{ fontSize: "11px" }}
          >
            {result.isCorrect ? "✓ Correct" : "✕ Incorrect"}
          </span>
        </div>

        {/* Question */}
        <p
          className="fw-semibold mb-3"
          style={{ color: "rgb(123, 31, 162)", fontSize: "0.95rem" }}
        >
          {result.question}
        </p>

        {/* Options */}
        <div className="d-flex flex-column gap-2">
          {allOptions.map((opt) => {
            const isCorrect = opt === result.correctAnswer;
            const isWrong = opt === result.selectedAnswer && !result.isCorrect;
            let cls = "result-option";
            let icon = "dot";
            if (isCorrect) {
              cls += " opt-correct";
              icon = "correct";
            } else if (isWrong) {
              cls += " opt-wrong";
              icon = "wrong";
            }
            return (
              <div key={opt} className={cls}>
                <ResultIcon type={icon} />
                {opt}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const QuizLogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const breadcrumbItems = [{ title: "Back to List", link: "/quiz-logs" }];
  const [detailData, setDetailData] = useState(null);

  const fetchDetail = async () => {
    try {
      const response = await QuizService.getLogDetail(id);
      setDetailData(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id) fetchDetail();
  }, [id]);

  if (!detailData) return null; // Loading state

  const pass = detailData.scorePercentage >= 60;

  // Map questionId to its options
  const optionsMap = {};
  detailData.answers.forEach((q) => {
    optionsMap[q.questionId] = [
      q.optionA,
      q.optionB,
      q.optionC,
      q.optionD,
    ].filter(Boolean);
  });

  const onRestart = () => {
    navigate(`/quiz/${detailData.quizSlug}`);
  };

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Page Form"
          pageTitle="Page"
          breadcrumbItems={breadcrumbItems}
        />

        <div className="">
          <div className="container">
            <Card className="border-0 shadow-sm rounded-4">
              <CardBody className="p-4">
                {/* Header Section */}
                <Row>
                  <Col>
                    <div className="">
                      <h5 className="fw-bold text-primary mb-3">
                        {detailData?.quizName}
                      </h5>

                      <div className="d-flex flex-column gap-3 text-muted small">
                        <span>
                          <i className="bi bi-person-circle me-2"></i>
                          {detailData?.userName}
                        </span>
                        <span>
                          <i className="bi bi-person-circle me-2"></i>
                          {detailData?.userEmail}
                        </span>
                        <span>
                          <i className="bi bi-telephone me-2"></i>
                          {detailData?.userPhone}
                        </span>

                        <span>
                          <i className="bi bi-calendar-event me-2"></i>
                          {formatDate(detailData?.submittedAt)}
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-4">
                      <ScoreRing
                        percentage={detailData?.scorePercentage}
                        pass={pass}
                      />

                      <div className="flex-grow-1">
                        <div
                          className="fw-bold mb-2"
                          style={{ fontSize: "2rem" }}
                        >
                          {detailData?.correctAnswers} /{" "}
                          {detailData?.totalQuestions}
                        </div>

                        <p className="text-muted mb-3">
                          Questions answered correctly
                        </p>

                        <span
                          className={`badge rounded-pill px-4 py-2 fs-6 ${
                            pass
                              ? "bg-success-subtle text-success border border-success"
                              : "bg-danger-subtle text-danger border border-danger"
                          }`}
                        >
                          {pass
                            ? "🎉 Congratulations! Passed"
                            : "Needs Improvement"}
                        </span>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Score Section */}
              </CardBody>
            </Card>

            <p
              className="text-uppercase fw-semibold mb-3"
              style={{
                fontSize: "11px",
                color: "black",
                letterSpacing: "0.15em",
              }}
            >
              Answer Breakdown
            </p>

            {detailData.answers.map((result, i) => (
              <QuestionCard
                key={result.questionId}
                result={result}
                index={i}
                allOptions={optionsMap[result.questionId]}
              />
            ))}

            {/* <div className="text-center">
              <button
                type="button"
                className="btn btn-dark"
                onClick={onRestart}
              >
                ↺ &nbsp; Retake Quiz
              </button>
            </div> */}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default QuizLogDetail;
